import type { GraphQLRequest } from '@apollo/client';
import uniq from 'lodash/uniq';
import qs from 'qs';

import type { ReportableError, ReportOptions } from '@change-corgi/core/errorReporter/common';
import { isPerimeterXHttpResponse } from '@change-corgi/core/perimeterx';

type Options = {
	fetch: typeof fetch;
	reportError: (error: ReportableError, options?: ReportOptions) => void;
	reportNetworkError: (error: ReportableError) => void;
	addOperationNamesToUri?: boolean;
};

function getRequestUrl(info: RequestInfo | URL) {
	if (typeof info === 'string') return info;
	if (info instanceof URL) return info.toString();
	return info.url;
}

function getRequestMethod(info: RequestInfo | URL, init?: RequestInit | undefined) {
	if (init?.method) return init.method;
	if (typeof info === 'string' || info instanceof URL) return 'GET';
	return info.method;
}

type GqlDetails = {
	batched: boolean;
	operationNames: readonly string[];
};
function isGraphQLRequestBatch(
	request: GraphQLRequest | readonly GraphQLRequest[],
): request is readonly GraphQLRequest[] {
	return Array.isArray(request);
}
function getRequestGqlDetails(init?: RequestInit | undefined): GqlDetails | undefined {
	if (!init?.body) return undefined;
	if (typeof init.body !== 'string') return undefined;
	try {
		const body = JSON.parse(init.body) as GraphQLRequest | readonly GraphQLRequest[];
		if (isGraphQLRequestBatch(body)) {
			return {
				batched: true,
				operationNames: body.map(({ operationName }) => operationName || 'unknown'),
			};
		}
		return {
			batched: false,
			operationNames: [body.operationName || 'unknown'],
		};
	} catch (e) {
		return undefined;
	}
}

function getOperationNames(bodyJson: BodyInit | undefined | null): readonly string[] {
	if (typeof bodyJson !== 'string') return [];
	try {
		const body = JSON.parse(bodyJson) as GraphQLRequest | GraphQLRequest[];
		const bodyArray = Array.isArray(body) ? body : [body];
		return bodyArray
			.map(({ operationName }) => operationName)
			.filter((operationName): operationName is string => !!operationName);
	} catch (e) {
		return [];
	}
}

function buildUrl(uri: RequestInfo | URL, operationNames: readonly string[] | undefined) {
	if (typeof uri !== 'string' || !operationNames || !operationNames.length) return uri;
	const [url, query] = uri.split('?');
	return `${url}?${qs.stringify(
		{
			...qs.parse(query),
			op: uniq(operationNames)
				.sort()
				.map((operationName) => {
					const count = operationNames.filter((name) => name === operationName).length;
					return count > 1 ? `${operationName}~${count}` : operationName;
				}),
		},
		{ arrayFormat: 'repeat' },
	)}`;
}

// eslint-disable-next-line max-lines-per-function
export function wrapFetch({
	fetch: originalFetch,
	reportError,
	reportNetworkError,
	addOperationNamesToUri,
}: Options): typeof fetch {
	// Patch fetch to ensure error includes relevant debugging info
	// eslint-disable-next-line max-lines-per-function
	return async function fetch(infoParam, init) {
		/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-argument */
		const info = addOperationNamesToUri ? buildUrl(infoParam as any, getOperationNames(init?.body)) : infoParam;
		try {
			const result = await originalFetch(info as any, init);
			if (!result.ok) {
				throw result;
			}
			return result;
		} catch (e) {
			if ((e as Response).status) {
				const response = e as Response;
				const requestId = response.headers?.get('x-request-id') || undefined;

				const gqlDetails = getRequestGqlDetails(init);

				// note: this will be reported alongside the reported errors in GqlClient for non-5xx errors
				void reportError(
					{
						// TODO switch to http-errors?
						error: new Error(getResponseErrorMessage(response)),
						params: {
							message: response.statusText,
							status: response.status,
							url: getRequestUrl(info as any),
							method: getRequestMethod(info as any, init),
							gqlDetails,
						},
						context: {
							requestId,
							graphql: gqlDetails && {
								batched: gqlDetails.batched,
								operationName: [...gqlDetails.operationNames].sort().join(','),
							},
						},
					},
					{
						// GQLResponseServerError is the one that should be reported as error
						// these are reported as warning so we can detect trends in GQL errors
						severity: 'warning',
					},
				);

				if (response.status < 500) {
					return response;
				}
				throw e;
			}
			void reportNetworkError({
				error: e as Error,
				params: {
					url: getRequestUrl(info as any),
					method: getRequestMethod(info as any, init),
					gqlDetails: getRequestGqlDetails(init),
				},
			});
			throw e;
		}
		/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-argument */
	};
}

function getResponseErrorMessage(response: Response) {
	return isPerimeterXHttpResponse(response)
		? `GraphQL query blocked by PerimeterX (HTTP ${response.status})`
		: `HTTP ${response.status} Error when running GraphQL query`;
}
